import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { supervisorService } from '../../application/SupervisorService';
import { PLAN_TYPE } from '../../constants/Subscriptions';
import { SupervisorActionType } from '../../context/supervisor/actions';
import { useSupervisorContext } from '../../context/supervisor/context';
import { Supervisor } from '../../domain/supervisor/Supervisor';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../organization/useRetrieveOrganization';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

type UseRetrieveAllSupervisorsResponse = ServiceResponse<Function, Supervisor[]>;

// Hooks act as 'Adapter' layer.
export const useRetrieveAllSupervisors = (): UseRetrieveAllSupervisorsResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: organization } = useRetrieveOrganization();
  const { supervisorDispatch, supervisorState } = useSupervisorContext();
  const { sessionExpiredLogout } = useErrorMiddleware();

  const { isLoading, refetch, isError } = useQuery(
    [`planner_supervisors`, activeProject!.id],
    () => supervisorService.fetchAll({ project: activeProject! }),
    {
      retry: false,
      enabled: activeProject && organization?.subscription.plan !== PLAN_TYPE.MONKEY,
      staleTime: Infinity,
      onSuccess: (supervisors: Supervisor[]) => {
        supervisorDispatch({
          type: SupervisorActionType.SEARCH_SUPERVISORS_SUCCESS,
          payload: { supervisors: supervisors },
        });
      },
      onError: (err: any) => {
        console.info(`Planner retrieve supervisors Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: true,
    isLoading,
    data: supervisorState.supervisors,
    run: refetch,
  };
};
